<template>
  <AModal :model-value="modelValue" size="lg" @update:model-value="$emit('update:modelValue', $event)">
    <template #title>
      <span class="wd-lookup__title">
        <i class="material-icons-round wd-lookup__title-icon">person_search</i>
        탈퇴 기록 조회
      </span>
    </template>
    <template #sub>
      "동의한 적 없다 / 계정이 사라졌다" 문의에 답할 때, 탈퇴 당시 이메일로 기록을 찾습니다.
      같은 이메일로 재가입한 계정은 새 번호를 받으므로 상세 화면에는 옛 기록이 보이지 않습니다.
    </template>

    <!--
      탈퇴자 이메일은 서버에 HMAC 해시로만 남습니다. 서버가 입력값을 정규화(대소문자·공백)하고
      해시로 비교하므로 여기서는 앞뒤 공백만 잘라 보냅니다 (docs/회원관리_API.md §3.5).
    -->
    <form class="wd-lookup__form" @submit.prevent="search">
      <input
        ref="emailInput"
        v-model="email"
        type="email"
        class="p-input"
        placeholder="탈퇴 당시 이메일"
        autocomplete="off"
        :disabled="loading"
      />
      <ABtn kind="primary" icon="search" type="submit" :loading="loading" :disabled="!email.trim()">
        {{ loading ? '조회 중...' : '조회' }}
      </ABtn>
    </form>

    <div v-if="loading" class="wd-lookup__loading">
      <div class="p-spinner"></div>
      <span>탈퇴 기록을 찾는 중...</span>
    </div>

    <div v-else-if="error" class="wd-lookup__error">
      <i class="material-icons-round">error_outline</i>
      <span>{{ error }}</span>
    </div>

    <template v-else-if="searched">
      <AEmpty
        v-if="results.length === 0"
        size="sm"
        title="탈퇴 기록이 없어요"
        sub="이 이메일로 남은 탈퇴 기록이 없습니다. 탈퇴 기록 도입 이전에 탈퇴했거나, 이메일이 다를 수 있습니다."
      />
      <div v-else class="wd-lookup__results">
        <div class="wd-lookup__results-head">
          <span class="wd-lookup__results-email">{{ searchedEmail }}</span>
          <APill kind="info">{{ results.length }}건</APill>
        </div>
        <WithdrawalInfoCard
          v-for="r in results"
          :key="r.logIdx"
          :record="r"
          show-user
          flat
          @view-user="viewUser"
        />
      </div>
    </template>

    <template #foot>
      <ABtn @click="$emit('update:modelValue', false)">닫기</ABtn>
    </template>
  </AModal>
</template>

<script>
import AModal from "@/components/Pineapple/AModal.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import APill from "@/components/Pineapple/APill.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
import WithdrawalInfoCard from "@/views/users/components/WithdrawalInfoCard.vue";

export default {
  name: "WithdrawalLookupModal",
  components: { AModal, ABtn, APill, AEmpty, WithdrawalInfoCard },
  props: {
    modelValue: { type: Boolean, default: false },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      email: "",
      searchedEmail: "",
      searched: false,
      loading: false,
      error: "",
      results: [],
    };
  },
  watch: {
    modelValue(open) {
      if (open) this.$nextTick(() => this.$refs.emailInput?.focus());
    },
  },
  methods: {
    async search() {
      const email = this.email.trim();
      if (!email) return;
      this.loading = true;
      this.error = "";
      try {
        const res = await this.$axios({
          method: "get",
          url: "/roles/withdrawals",
          params: { email },
          loadingBar: false,
        });
        this.results = Array.isArray(res.data) ? res.data : [];
        // 최근 탈퇴가 위로 오도록. 서버 정렬을 믿지 않고 여기서 고정합니다.
        this.results.sort((a, b) => new Date(b.withdrawnAt) - new Date(a.withdrawnAt));
        this.searchedEmail = email;
        this.searched = true;
      } catch (e) {
        this.results = [];
        this.error = e.response?.data?.detail || "탈퇴 기록 조회에 실패했습니다.";
      } finally {
        this.loading = false;
      }
    },
    viewUser(userIdx) {
      this.$emit("update:modelValue", false);
      this.$router.push({ name: "UserDetail", query: { idx: userIdx } });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.wd-lookup {
  &__title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  &__title-icon {
    color: $p-point-deep;
    font-size: 18px !important;
  }

  &__form {
    display: flex;
    gap: 8px;
    margin-bottom: 16px;

    > .p-input {
      flex: 1;
      min-width: 0;
    }
  }

  &__loading {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 12px;
    color: $p-ink-mid;
  }

  &__error {
    display: flex;
    gap: 8px;
    padding: 12px 14px;
    background: $p-err-soft;
    border-left: 3px solid $p-err;
    border-radius: $p-radius-md;
    color: $p-ink2;
    font-size: 12px;
    line-height: 1.5;

    > i {
      color: $p-err;
      font-size: 16px;
      flex-shrink: 0;
    }
  }

  &__results {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__results-head {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__results-email {
    font-family: $p-font-mono;
    font-size: 12px;
    color: $p-ink2;
  }
}
</style>
